import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const SignUpScreen: React.FC = () => {
    const navigate = useNavigate();
    const [visiblePassword, setVisiblePassword] = useState<boolean>(false);
    const [visibleConfirmPassword, setVisibleConfirmPassword] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [alertSev, setAlertSev] = useState("error");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTermsAccepted(e.target.checked);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage(null);
        setPasswordError(null);

        const validations = [
            {
                condition: !formData.username.trim() || !formData.email.trim(),
                message: 'Username and email cannot be empty',
            },
            {
                condition: !/^[A-Za-z0-9]+$/.test(formData.username),
                message: 'Username can only contain letters and numbers',
            },
            {
                condition: formData.password.length < 8,
                message: 'Password must be at least 8 characters long',
            },
            {
                condition: formData.password !== formData.confirmPassword,
                message: "Passwords don't match",
            },
            {
                condition: !termsAccepted,
                message: 'Please accept the terms and conditions',
            },
        ];

        for (const validation of validations) {
            if (validation.condition) {
                setErrorMessage(validation.message);
                return;
            }
        }

        setIsLoading(true);
        try {
            const response = await axios.post('/pool/auth/register', {
                userName: formData.username,
                email: formData.email,
                password: formData.password,
            });
            if (response.data.status) {
                setAlertSev("success");
                setErrorMessage(response.data.message);
                navigate('/login');
            } else {
                setErrorMessage(response.data.message);
            }
        } catch (error: any) {
            if (error.response) {
                setErrorMessage('Error: Unable to register.');
            } else if (error.request) {
                setErrorMessage('Network error: No response received from server');
            } else {
                setErrorMessage('An error occurred while processing your request');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setErrorMessage(null);
    };


    return (
        <div className="relative flex items-center justify-center min-h-screen bg-gradient-to-br from-[#FAF8F5] via-[#E5DDD0] to-[#D5C5A9] p-4 overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute top-20 left-10 w-64 h-64 bg-[#E5BC75]/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#8A540E]/10 rounded-full blur-3xl animate-pulse delay-700"></div>
            <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-[#D5C5A9]/30 rounded-full blur-2xl"></div>

            {/* Decorative geometric shapes */}
            <div className="absolute top-10 right-20 w-16 h-16 border-4 border-[#E5BC75]/30 rotate-45 animate-spin-slow"></div>
            <div className="absolute bottom-32 left-16 w-12 h-12 bg-[#8A540E]/20 rounded-lg rotate-12"></div>
            <div className="absolute top-1/3 right-1/4 w-8 h-8 bg-[#5E482C]/20 rounded-full"></div>
            <div className="absolute bottom-1/4 left-1/3 w-10 h-10 border-2 border-[#E5BC75]/40 rounded-full"></div>

            {/* Main form card */}
            <div className="relative bg-white/90 backdrop-blur-md p-8 rounded-2xl shadow-2xl w-full max-w-md border-2 border-[#D5C5A9]/50 z-10">
                <div className="flex justify-center ">
                    <div className="relative mb-3">
                        <img
                            src="/assets/cashlabblk.png"
                            alt="Logo"
                            className=" h-8 object-contain"
                        />
                    </div>
                </div>
                <div className="border-t border-[#D5C5A9]/50 mb-6"></div>

                <h2 className="text-3xl font-bold text-center text-[#412F19] mb-2 tracking-wide">SIGN UP</h2>
                <p className="text-center text-[#5E482C] text-sm mb-8">Create your account to get started</p>

                <form onSubmit={handleSubmit}>
                    {/* Name field */}
                    <div className="mb-5">
                        <label className="block text-[#5E482C] mb-2 font-medium text-sm" htmlFor="name">
                            Name *
                        </label>
                        <input
                            className="w-full p-3 rounded-lg border-2 border-[#D5C5A9] bg-white text-[#412F19] focus:outline-none focus:ring-2 focus:ring-[#8A540E] focus:border-transparent transition-all font-medium placeholder:text-[#5E482C]/50"
                            type="text"
                            id="name"
                            name="username"
                            placeholder="Username"
                            value={formData.username}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    {/* Email field */}
                    <div className="mb-5">
                        <label className="block text-[#5E482C] mb-2 font-medium text-sm" htmlFor="email">
                            Email *
                        </label>
                        <input
                            className="w-full p-3 rounded-lg border-2 border-[#D5C5A9] bg-white text-[#412F19] focus:outline-none focus:ring-2 focus:ring-[#8A540E] focus:border-transparent transition-all font-medium placeholder:text-[#5E482C]/50"
                            type="email"
                            id="email"
                            name="email"
                            placeholder="Email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    {/* Password field */}
                    <div className="mb-5 relative">
                        <label className="block text-[#5E482C] mb-2 font-medium text-sm" htmlFor="password">
                            Password *
                        </label>
                        <div className="relative">
                            <input
                                id="password"
                                type={visiblePassword ? "text" : "password"}
                                className="w-full p-3 rounded-lg border-2 border-[#D5C5A9] bg-white text-[#412F19] focus:outline-none focus:ring-2 focus:ring-[#8A540E] focus:border-transparent transition-all font-medium placeholder:text-[#5E482C]/50"
                                name="password"
                                placeholder="Password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 end-0 flex items-center px-3 cursor-pointer text-[#E5BC75] hover:text-[#8A540E] transition-colors rounded-e-lg focus:outline-none"
                                onClick={() => setVisiblePassword(!visiblePassword)}
                            >
                                <svg
                                    className="shrink-0 w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    viewBox="0 0 24 24"
                                >
                                    {visiblePassword ? (
                                        <>
                                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </>
                                    ) : (
                                        <>
                                            <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                                            <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                                            <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                                            <line x1="2" y1="2" x2="22" y2="22" />
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Confirm Password field */}
                    <div className="mb-5 relative">
                        <label className="block text-[#5E482C] mb-2 font-medium text-sm" htmlFor="confirmPassword">
                            Confirm Password *
                        </label>
                        <div className="relative">
                            <input
                                id="confirmPassword"
                                type={visibleConfirmPassword ? "text" : "password"}
                                className="w-full p-3 rounded-lg border-2 border-[#D5C5A9] bg-white text-[#412F19] focus:outline-none focus:ring-2 focus:ring-[#8A540E] focus:border-transparent transition-all font-medium placeholder:text-[#5E482C]/50"
                                placeholder="Enter confirm password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                required
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 end-0 flex items-center px-3 cursor-pointer text-[#E5BC75] hover:text-[#8A540E] transition-colors rounded-e-lg focus:outline-none"
                                onClick={() => setVisibleConfirmPassword(!visibleConfirmPassword)}
                            >
                                <svg
                                    className="shrink-0 w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    viewBox="0 0 24 24"
                                >
                                    {visibleConfirmPassword ? (
                                        <>
                                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </>
                                    ) : (
                                        <>
                                            <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                                            <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                                            <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                                            <line x1="2" y1="2" x2="22" y2="22" />
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Terms and conditions checkbox */}
                    <div className="mb-6">
                        <label className="flex items-center text-[#5E482C] cursor-pointer hover:text-[#412F19] transition-colors text-sm">
                            <input
                                type="checkbox"
                                checked={termsAccepted}
                                onChange={handleCheckboxChange}
                                className="mr-2 w-4 h-4 accent-[#8A540E] cursor-pointer rounded"
                            />
                            <span>I accept the terms and conditions</span>
                        </label>
                    </div>

                    {/* Submit button */}
                    <button
                        type="submit"
                        className="w-full p-3 rounded-lg font-bold text-white bg-gradient-to-r from-[#E5BC75] via-[#8A540E] to-[#8A540E] hover:shadow-xl hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!termsAccepted || isLoading}
                    >
                        {isLoading ? (
                            <span className="flex items-center justify-center">
                                <svg
                                    aria-hidden="true"
                                    role="status"
                                    className="inline w-4 h-4 mr-2 text-white animate-spin"
                                    viewBox="0 0 100 101"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                        fill="#E5E7EB"
                                    />
                                    <path
                                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                SIGNING UP...
                            </span>
                        ) : (
                            "SIGN UP"
                        )}
                    </button>
                </form>

                {/* Divider */}
                <div className="flex items-center gap-4 my-6">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#D5C5A9] to-transparent"></div>
                    <span className="text-[#5E482C] text-xs font-medium">OR</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#D5C5A9] to-transparent"></div>
                </div>

                {/* Sign in link */}
                <div className="text-center">
                    <p className="text-[#5E482C] text-sm">
                        Already have an account?{" "}
                        <span
                            className="font-bold cursor-pointer text-[#E5BC75] hover:text-[#8A540E] transition-colors"
                            onClick={() => navigate('/login')}
                        >
                            Sign in →
                        </span>
                    </p>
                </div>
            </div>

            {/* Notification snackbar */}
            {errorMessage && (
                <div
                    className={`fixed bottom-5 left-5 p-4 rounded-lg shadow-xl w-full max-w-sm flex items-center gap-3 z-50 ${alertSev === "success"
                            ? "bg-[#E5BC75] border-2 border-[#8A540E] text-[#412F19]"
                            : "bg-red-50 border-2 border-red-500 text-red-700"
                        }`}
                    role="alert"
                >
                    <div className="flex-1 text-sm font-medium">{errorMessage}</div>
                    <button className="text-current hover:opacity-70 transition-opacity" onClick={handleCloseSnackbar}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            className="w-5 h-5"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};

export default SignUpScreen;
